<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { getters as auth, logout } from '../services/platform-auth.service';

const lockupWhite = '/brand/lockup-white.svg';

const router = useRouter();

const navItems = computed(() =>
    router
        .getRoutes()
        .filter((r) => r.meta?.sidebar)
        .sort((a, b) => (a.meta.sidebar.order ?? 99) - (b.meta.sidebar.order ?? 99))
        .map((r) => ({
            name: r.name,
            label: r.meta.sidebar.label,
            icon: r.meta.icon || 'icon-nav',
        }))
);

function signOut() {
    logout().catch((err) => console.error('Sign out failed', err));
}
</script>

<template>
    <div class="pf">
        <aside class="pf__sidebar">
            <div class="pf__brand">
                <img :src="lockupWhite" alt="Hive" class="pf__logo" />
                <span class="pf__badge">Platform</span>
            </div>
            <nav class="pf__nav">
                <router-link
                    v-for="item in navItems"
                    :key="item.name"
                    :to="{ name: item.name }"
                    class="pf__nav-item"
                    active-class="pf__nav-item--active"
                >
                    <j-icon :icon-id="item.icon" class="pf__nav-icon" />
                    <span>{{ item.label }}</span>
                </router-link>
            </nav>
        </aside>

        <div class="pf__main">
            <header class="pf__topbar">
                <span class="pf__user">{{ auth.profile?.displayName || auth.profile?.email }}</span>
                <j-button variant="ghost" size="small" @click="signOut">Sign out</j-button>
            </header>
            <main class="pf__content">
                <slot></slot>
            </main>
        </div>

        <div
            v-if="auth.isLoggingOut"
            class="pf__logout-overlay"
            role="status"
            aria-live="polite"
        >
            <span class="pf__logout-spinner" aria-hidden="true"></span>
            <p>Signing out…</p>
        </div>
    </div>
</template>

<style scoped>
.pf {
    display: flex;
    width: 100%;
    min-height: 100vh;
}
.pf__sidebar {
    width: 240px;
    flex-shrink: 0;
    background: var(--brand-primary, #0f172a);
    color: white;
    display: flex;
    flex-direction: column;
    padding: 20px 12px;
}
.pf__brand {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px 24px;
}
.pf__logo {
    height: 26px;
}
.pf__badge {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: rgba(255, 255, 255, 0.14);
    border-radius: 6px;
    padding: 2px 7px;
}
.pf__nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.pf__nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.82);
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
}
.pf__nav-item:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
}
.pf__nav-item--active {
    background: rgba(255, 255, 255, 0.16);
    color: white;
}
.pf__nav-icon {
    width: 18px;
    height: 18px;
    fill: currentColor;
}
.pf__main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background: var(--neutral-50, #f8fafc);
}
.pf__topbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 12px 24px;
    border-bottom: 1px solid var(--neutral-100, #f1f5f9);
    background: white;
}
.pf__user {
    font-size: 0.8125rem;
    color: var(--neutral-600, #64748b);
}
.pf__content {
    flex: 1;
    min-width: 0;
}
.pf__logout-overlay {
    position: fixed;
    inset: 0;
    background: var(--brand-primary, #0f172a);
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    z-index: 9999;
}
.pf__logout-spinner {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: white;
    animation: pf-spin 0.9s linear infinite;
}
@keyframes pf-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
